// src/assets/js/quizzes/bom/QuizzesState.js
// BOM: estado + lógica pura (sin DOM, sin fetch, sin render)

// Número de quizzes por página (paginación)
const DEFAULT_PAGE_SIZE = 10;

export class QuizzesState {
	/**
	 * @param {object} [initial]
	 * @param {'subjects'|'list'} [initial.viewMode]
	 * @param {string|null} [initial.subjectId]
	 * @param {string|null} [initial.regionId]
	 * @param {string} [initial.query]
	 * @param {number} [initial.pageSize]
	 * @param {number} [initial.visibleCount]
	 */
	constructor(initial = {}) {
		this._listeners = new Set();

		this._state = {
			viewMode: initial.viewMode || 'subjects',
			subjectId: initial.subjectId ?? null,
			regionId: initial.regionId ?? null,
			query: (initial.query || '').trim(),
			pageSize: Number.isFinite(initial.pageSize) ? initial.pageSize : DEFAULT_PAGE_SIZE,
			visibleCount: Number.isFinite(initial.visibleCount)
				? initial.visibleCount
				: Number.isFinite(initial.pageSize)
					? initial.pageSize
					: DEFAULT_PAGE_SIZE,
		};
	}

	/* =========================
	   Suscripción / eventos
	========================= */

	/**
	 * @param {(snapshot: object, meta?: object) => void} fn
	 * @returns {() => void} unsubscribe
	 */
	subscribe(fn) {
		if (typeof fn !== 'function') return () => {};
		this._listeners.add(fn);
		// Primera notificación opcional (útil para pintar inicial)
		fn(this.getSnapshot(), { type: 'init' });
		return () => this._listeners.delete(fn);
	}

	_notify(meta) {
		const snap = this.getSnapshot();
		for (const fn of this._listeners) fn(snap, meta);
	}

	/* =========================
	   Lectura de estado
	========================= */

	getSnapshot() {
		// copia inmutable para evitar mutaciones externas
		return { ...this._state };
	}

	get viewMode() {
		return this._state.viewMode;
	}
	get subjectId() {
		return this._state.subjectId;
	}
	get regionId() {
		return this._state.regionId;
	}
	get query() {
		return this._state.query;
	}
	get pageSize() {
		return this._state.pageSize;
	}
	get visibleCount() {
		return this._state.visibleCount;
	}

	/* =========================
	   Mutaciones (API pública)
	========================= */

	setViewMode(viewMode) {
		const next = viewMode === 'list' ? 'list' : 'subjects';
		if (this._state.viewMode === next) return;

		this._state.viewMode = next;
		// Cambiar de vista no obliga a resetear filtros, pero sí suele resetear paginación
		this.resetPagination({ silent: true });
		this._notify({ type: 'viewMode', value: next });
	}

	/**
	 * Toggle de materia:
	 * - si clicas la misma, se deselecciona
	 * - al seleccionar materia -> vista 'list'
	 * - al deseleccionar -> vista 'subjects' (si no hay query activa)
	 */
	toggleSubject(subjectId) {
		const id = subjectId ? String(subjectId) : null;
		const prev = this._state.subjectId;

		this._state.subjectId = prev === id ? null : id;

		this.resetPagination({ silent: true });

		// lógica UX: si hay materia -> lista, si no hay -> subjects (salvo búsqueda activa)
		if (this._state.subjectId) {
			this._state.viewMode = 'list';
		} else if (!this._state.query) {
			this._state.viewMode = 'subjects';
		}

		this._notify({ type: 'subject', value: this._state.subjectId });
	}

	setSubject(subjectId) {
		const id = subjectId ? String(subjectId) : null;
		if (this._state.subjectId === id) return;

		this._state.subjectId = id;
		this.resetPagination({ silent: true });

		this._applyAutoViewMode();

		this._notify({ type: 'subject', value: id });
	}

	clearSubject() {
		if (this._state.subjectId === null) return;
		this._state.subjectId = null;
		this.resetPagination({ silent: true });

		this._applyAutoViewMode();

		this._notify({ type: 'subject', value: null });
	}

	setRegion(regionId) {
		const id = regionId ? String(regionId) : null;
		if (this._state.regionId === id) return;

		this._state.regionId = id;
		this.resetPagination({ silent: true });

		// si hay materia elegida, seguimos en lista; si no, seguimos en subjects
		this._applyAutoViewMode();

		this._notify({ type: 'region', value: id });
	}

	clearRegion() {
		if (this._state.regionId === null) return;
		this._state.regionId = null;
		this.resetPagination({ silent: true });

		this._applyAutoViewMode();

		this._notify({ type: 'region', value: null });
	}

	setQuery(text) {
		const q = String(text || '').trim();
		if (this._state.query === q) return;

		this._state.query = q;
		this.resetPagination({ silent: true });

		// UX: si hay búsqueda -> lista; si no hay búsqueda y no hay materia -> subjects
		this._applyAutoViewMode();

		this._notify({ type: 'query', value: q });
	}

	clearQuery() {
		if (!this._state.query) return;
		this._state.query = '';
		this.resetPagination({ silent: true });

		this._applyAutoViewMode();

		this._notify({ type: 'query', value: '' });
	}

	setPageSize(n) {
		const next = Number(n);
		if (!Number.isFinite(next) || next <= 0) return;
		if (this._state.pageSize === next) return;

		this._state.pageSize = next;
		this._state.visibleCount = next;
		this._notify({ type: 'pageSize', value: next });
	}

	/**
	 * "Cargar más" (incremental)
	 * @param {number} [step] por defecto pageSize
	 */
	loadMore(step) {
		const inc = Number.isFinite(step) ? Number(step) : this._state.pageSize;
		if (!Number.isFinite(inc) || inc <= 0) return;

		this._state.visibleCount += inc;
		this._notify({ type: 'loadMore', value: this._state.visibleCount });
	}

	resetPagination({ silent = false } = {}) {
		this._state.visibleCount = this._state.pageSize;
		if (!silent) this._notify({ type: 'resetPagination' });
	}

	/**
	 * Reset completo (útil para botón "Limpiar filtros")
	 */
	resetAll() {
		this._state.viewMode = 'subjects';
		this._state.subjectId = null;
		this._state.regionId = null;
		this._state.query = '';
		this._state.visibleCount = this._state.pageSize;

		this._applyAutoViewMode();

		this._notify({ type: 'resetAll' });
	}

	/* =========================
	   Helpers de consulta
	========================= */

	/**
	 * @returns {boolean} true si hay cualquier filtro activo
	 */
	hasFilters() {
		return Boolean(this._state.subjectId || this._state.regionId || this._state.query);
	}

	/**
	 * El controller/servicio lo usará para filtrar quizzes
	 */
	getFilterParams() {
		return {
			subjectId: this._state.subjectId,
			regionId: this._state.regionId,
			query: this._state.query,
		};
	}

	/* =========================
	   Helpers privados
	========================= */
	_applyAutoViewMode() {
		const hasSubject = !!this._state.subjectId;
		const hasQuery = !!(this._state.query && this._state.query.trim());

		this._state.viewMode = hasSubject || hasQuery ? 'list' : 'subjects';
		console.log('[QuizzesState] viewMode:', this._state.viewMode);
	}
}
